import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'urql';

import { CreatorDiscoveryInstagramAccount } from '@frontend/app/hooks';
import { DiscoverInstagramCreatorsQuery, InstagramCreatorGender } from '@frontend/gql/social/graphql';
import { QueryVariables } from '../../types';
import { discoverInstagramCreators } from '../graphql';

export const useInstagramCreatorSearchData = (
  queryVariables: QueryVariables,
  clientId: string,
  selectedInstagramAccount?: CreatorDiscoveryInstagramAccount,
  defaultFiltersResolved?: boolean,
) => {
  const [searchResults, setSearchResults] = useState<DiscoverInstagramCreatorsQuery | null>(null);
  const [refetching, setRefetching] = useState(false);
  const [isPaginationFetching, setIsPaginationFetching] = useState(false);
  const { igUserId } = selectedInstagramAccount ?? {};

  const [{ data, fetching, error }, refetch] = useQuery({
    query: discoverInstagramCreators,
    variables: {
      ...queryVariables,
      igUserId: igUserId || '', // Use the passed igUserId, fallback to empty string or handle as needed
      clientId,
      genders: queryVariables.genders?.filter((g) => g !== 'ALL') as InstagramCreatorGender[],
      majorAudienceGenders: queryVariables.majorAudienceGenders?.filter((g) => g !== 'ALL') as InstagramCreatorGender[],
    },
    pause: !igUserId || defaultFiltersResolved === false, // Pause if no igUserId OR if default filters not resolved
  });

  useEffect(() => {
    if (!fetching) {
      setRefetching(false);
      setIsPaginationFetching(false);
    }
  }, [fetching]);

  // Process query results
  useEffect(() => {
    if (data?.discoverInstagramCreators?.creators) {
      setSearchResults(data);
    }
  }, [data, queryVariables]);

  // Handle search submission
  const handleRefresh = useCallback(() => {
    setRefetching(true);
    refetch({ requestPolicy: 'network-only' });
  }, [refetch]);

  // Handle pagination
  const handlePagination = useCallback(() => {
    setIsPaginationFetching(true);
    refetch({ requestPolicy: 'network-only' });
  }, [refetch]);

  return {
    data,
    fetching: fetching || defaultFiltersResolved === false, // Show loading while waiting for default filters
    error,
    searchResults,
    handleSearch: handleRefresh,
    handlePagination,
    refetch,
    refetching,
    isPaginationFetching,
  };
};
